/** stok — Procurement: suppliers, purchase orders, goods receipts. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enStokProcurement = {
  // ---- Status labels (badges; text carries the meaning)
  'stok.procStatus.po.draft': 'Draft',
  'stok.procStatus.po.pending_approval': 'Awaiting approval',
  'stok.procStatus.po.approved': 'Approved',
  'stok.procStatus.po.partially_received': 'Partially received',
  'stok.procStatus.po.received': 'Received',
  'stok.procStatus.po.closed': 'Closed',
  'stok.procStatus.po.cancelled': 'Cancelled',
  'stok.procStatus.gr.draft': 'Draft',
  'stok.procStatus.gr.posted': 'Posted',
  'stok.procStatus.gr.cancelled': 'Cancelled',

  // ---- Suppliers
  'stok.suppliers.title': 'Suppliers',
  'stok.suppliers.subtitle': 'Shared by every branch of the business.',
  'stok.suppliers.add': 'Add supplier',
  'stok.suppliers.readOnly':
    'Read only — changing suppliers needs the purchasing permission for the whole business.',
  'stok.suppliers.listTitle': 'Supplier list',
  'stok.suppliers.listDesc': 'Who you buy from, with contacts and payment terms.',
  'stok.suppliers.searchPlaceholder': 'Code, name, contact, phone or email',
  'stok.suppliers.empty': 'No suppliers yet — add one to start creating purchase orders.',
  'stok.suppliers.emptyReadOnly': 'No suppliers yet — they appear once purchasing adds them.',
  'stok.suppliers.code': 'Code',
  'stok.suppliers.codeHint': 'Short and unique, e.g. SUP-01. Cannot be changed later.',
  'stok.suppliers.codeLocked': 'The code cannot be changed.',
  'stok.suppliers.contact': 'Contact person',
  'stok.suppliers.phone': 'Phone',
  'stok.suppliers.email': 'Email',
  'stok.suppliers.taxId': 'Tax ID (NPWP)',
  'stok.suppliers.address': 'Address',
  'stok.suppliers.terms': 'Payment terms',
  'stok.suppliers.termsLabel': 'Payment terms (days)',
  'stok.suppliers.termsHint': '0 = cash on delivery; at most 365 days.',
  'stok.suppliers.termsCash': 'Cash',
  'stok.suppliers.termsDays': '{days} days',
  'stok.suppliers.activeLabel': 'Active',
  'stok.suppliers.activeHint': 'Inactive suppliers cannot be chosen for new purchase orders.',
  'stok.suppliers.editNamed': 'Edit {name}',
  'stok.suppliers.createTitle': 'New supplier',
  'stok.suppliers.editTitle': 'Edit supplier',
  'stok.suppliers.formDesc': 'Suppliers are shared by every branch.',
  'stok.suppliers.createSubmit': 'Add supplier',
  'stok.suppliers.saved': 'Supplier {name} saved.',
  'stok.suppliers.err.code': 'Use letters, digits, dot, dash or underscore (at most 20).',
  'stok.suppliers.err.codeTaken': 'Another supplier already uses this code.',
  'stok.suppliers.err.email': 'Enter a valid email address.',
  'stok.suppliers.err.terms': 'Enter a whole number of days from 0 to 365.',

  // ---- Purchase orders
  'stok.po.title': 'Purchase orders',
  'stok.po.subtitle': 'Orders to suppliers for this branch, newest first.',
  'stok.po.create': 'New order',
  'stok.po.readOnly': 'Read only — creating and changing orders needs the purchasing permission.',
  'stok.po.noPermission': 'You do not have access to purchase orders in this branch.',
  'stok.po.noManage': 'Ask a purchasing user to create the order.',
  'stok.po.listTitle': 'Orders',
  'stok.po.listDesc': 'Open an order to see its lines, approval and receiving progress.',
  'stok.po.searchPlaceholder': 'PO number or supplier',
  'stok.po.statusFilter': 'Filter by status',
  'stok.po.empty': 'No purchase orders yet — create the first order for a supplier.',
  'stok.po.emptyReadOnly': 'No purchase orders yet — they appear once purchasing creates them.',
  'stok.po.emptyFiltered': 'No orders with this status.',
  'stok.po.number': 'Order no.',
  'stok.po.supplier': 'Supplier',
  'stok.po.expectedDate': 'Expected delivery',
  'stok.po.lineCount': 'Lines',
  'stok.po.total': 'Total',
  'stok.po.backToList': 'All purchase orders',
  'stok.po.newTitle': 'New purchase order',
  'stok.po.newSubtitle': 'Saved as a draft; submit it for approval before it can be received.',
  'stok.po.detailTitle': 'Purchase order',
  'stok.po.editTitle': 'Edit draft {number}',
  'stok.po.editSubtitle': 'Saving replaces every line of the draft.',
  'stok.po.headTitle': 'Order details',
  'stok.po.headDesc': 'Supplier, expected delivery date and notes for the supplier.',
  'stok.po.pickSupplier': 'Choose a supplier',
  'stok.po.notes': 'Notes',
  'stok.po.linesTitle': 'Lines',
  'stok.po.linesFormDesc':
    'Quantity in the chosen unit, price per one unit before tax. At most {max} lines.',
  'stok.po.linesDesc': 'Ordered and received quantities in the unit of each line.',
  'stok.po.lineLabel': 'Line {n}',
  'stok.po.removeLine': 'Remove line {n}',
  'stok.po.addLine': 'Add line',
  'stok.po.pickItem': 'Choose an item',
  'stok.po.qty': 'Quantity',
  'stok.po.unit': 'Unit',
  'stok.po.priceLabel': 'Price per {unit} ({currency})',
  'stok.po.priceHint': 'Whole amount, without separators.',
  'stok.po.lineTotal': 'Line total',
  'stok.po.totalLines': 'Total ({count} lines)',
  'stok.po.draftNote': 'Drafts can still be changed until they are submitted for approval.',
  'stok.po.saveDraft': 'Save draft',
  'stok.po.created': 'Purchase order {number} saved as a draft.',
  'stok.po.saved': 'Purchase order {number} saved.',
  'stok.po.noSuppliers': 'There is no active supplier yet — add one first:',
  'stok.po.noItems': 'There is no active item yet — add one first:',
  'stok.po.err.supplier': 'Choose a supplier.',
  'stok.po.err.date': 'Enter a complete date.',
  'stok.po.err.notes': 'Use at most 500 characters.',
  'stok.po.err.noLines': 'Add at least one line.',
  'stok.po.err.tooManyLines': 'An order can have at most 100 lines.',
  'stok.po.err.item': 'Choose an item.',
  'stok.po.err.qty': 'Enter a quantity greater than 0 (at most 3 decimals).',
  'stok.po.err.unit': 'Choose a unit.',
  'stok.po.err.precision': 'This quantity cannot be converted exactly — use another unit.',
  'stok.po.err.price': 'Enter the price as a whole number (0 or more).',
  'stok.po.err.tooLarge': 'The amount is too large.',
  'stok.po.err.duplicate': 'This item and unit are already on another line.',
  'stok.po.factsTitle': 'Order',
  'stok.po.factsDesc': 'Who created and approved it, and when it is expected.',
  'stok.po.createdBy': 'Created by',
  'stok.po.approvedBy': 'Approved by',
  'stok.po.cancelReason': 'Cancellation reason',
  'stok.po.closeReason': 'Closing reason',
  'stok.po.ordered': 'Ordered',
  'stok.po.received': 'Received',
  'stok.po.remaining': 'Remaining',
  'stok.po.unitPrice': 'Unit price',
  'stok.po.progress': '{received} / {ordered} {unit}',
  'stok.po.editDraft': 'Edit draft',
  'stok.po.approved': 'Purchase order {number} approved.',
  'stok.po.submit': 'Submit for approval',
  'stok.po.submitting': 'Submitting…',
  'stok.po.submitTitle': 'Submit order {number} for approval?',
  'stok.po.submitBody':
    'The order to {supplier} can no longer be edited while it waits for approval.',
  'stok.po.submitWarning':
    'Check the quantities, prices and budget line; the approval steps follow the policy for this amount.',
  'stok.po.submitted': 'Purchase order {number} submitted for approval.',
  'stok.po.draftSubmitNote':
    'Draft — submit it for approval when it is complete; it can be received once approved.',
  'stok.po.awaitingSubmit': 'Draft — purchasing submits it for approval.',
  'stok.po.pendingNote': 'Waiting for approval — see the approval steps below.',
  'stok.po.rejectedTitle': 'The last approval was rejected.',
  'stok.po.rejectedBy': 'Rejected by {name} · {time}',
  'stok.po.rejectedHelp': 'The order is a draft again — correct it and submit it again.',
  'stok.po.sourceRequest': 'From purchase request',
  'stok.po.openRequest': 'Open the purchase request',
  'stok.po.cancel': 'Cancel order',
  'stok.po.cancelling': 'Cancelling…',
  'stok.po.cancelTitle': 'Cancel order {number}?',
  'stok.po.cancelBody': 'The order can no longer be received. Stock does not change.',
  'stok.po.cancelBodyCommitted':
    'The order can no longer be received and its open approval or budget commitment is released. Stock does not change.',
  'stok.po.cancelled': 'Purchase order {number} cancelled.',
  'stok.po.close': 'Close order',
  'stok.po.closing': 'Closing…',
  'stok.po.closeTitle': 'Close order {number}?',
  'stok.po.closeBody':
    'The remaining quantity will not be delivered. Goods already received stay in stock.',
  'stok.po.closed': 'Purchase order {number} closed.',
  'stok.po.reason': 'Reason',
  'stok.po.receive': 'Receive goods',

  // ---- Goods receipts
  'stok.gr.title': 'Receiving',
  'stok.gr.subtitle': 'Goods delivered against approved purchase orders.',
  'stok.gr.receiveGoods': 'Receive goods',
  'stok.gr.readOnly': 'Read only — recording deliveries needs the receiving permission.',
  'stok.gr.noPermission': 'Ask a user with the receiving permission to record the delivery.',
  'stok.gr.readyTitle': 'Ready to receive',
  'stok.gr.readyDesc': 'Approved orders that still have goods to come.',
  'stok.gr.readyEmpty': 'No orders waiting — approved purchase orders appear here.',
  'stok.gr.readyNoPermission': 'You do not have access to purchase orders in this branch.',
  'stok.gr.readyMeta': '{count} lines · expected {date}',
  'stok.gr.receiveShort': 'Receive',
  'stok.gr.receiveNamed': 'Receive order {number}',
  'stok.gr.listTitle': 'Receipts',
  'stok.gr.listDesc': 'Drafts can still be changed; posted receipts are in stock.',
  'stok.gr.searchPlaceholder': 'Receipt number, PO or supplier',
  'stok.gr.statusFilter': 'Filter by status',
  'stok.gr.empty': 'No receipts yet — receive an approved order to record a delivery.',
  'stok.gr.emptyFiltered': 'No receipts with this status.',
  'stok.gr.number': 'Receipt no.',
  'stok.gr.po': 'Purchase order',
  'stok.gr.direct': 'Direct (no PO)',
  'stok.gr.receivedAt': 'Received at',
  'stok.gr.backToList': 'All receipts',
  'stok.gr.newTitle': 'Receive goods',
  'stok.gr.newSubtitle': 'Record what arrived; stock changes only when the receipt is posted.',
  'stok.gr.pickTitle': 'Which order arrived?',
  'stok.gr.pickDesc': 'Choose the purchase order the delivery belongs to.',
  'stok.gr.pickPo': 'Choose an order',
  'stok.gr.pickContinue': 'Continue',
  'stok.gr.notReceivable': 'Order {number} cannot be received (it is not approved or is finished).',
  'stok.gr.openPo': 'Open the order',
  'stok.gr.noLocations': 'This branch has no active stock location yet — add one first:',
  'stok.gr.detailTitle': 'Goods receipt',
  'stok.gr.detailSubtitle': '{supplier} · order {po}',
  'stok.gr.headTitle': 'Delivery for order {number}',
  'stok.gr.headDesc': 'From {supplier}. Choose where the goods are stored.',
  'stok.gr.deliveryRef': 'Delivery note no.',
  'stok.gr.deliveryRefHint': "The number on the supplier's delivery note.",
  'stok.gr.notes': 'Notes',
  'stok.gr.linesTitle': 'Lines',
  'stok.gr.linesDesc':
    'Leave a line empty when nothing of it arrived. Quantities in the order unit.',
  'stok.gr.viewLinesDesc': 'Delivered includes rejected goods; only accepted goods enter stock.',
  'stok.gr.fillAll': 'Receive all remaining',
  'stok.gr.fillRemaining': 'Fill remaining',
  'stok.gr.fillRemainingNamed': 'Fill remaining of {item}',
  'stok.gr.lineLabel': 'Line {n}: {item}',
  'stok.gr.lineFacts': 'Ordered {ordered} · already received {received} · remaining {remaining}',
  'stok.gr.lineDone': 'Fully received',
  'stok.gr.delivered': 'Delivered ({unit})',
  'stok.gr.deliveredHint': 'Everything that arrived, including rejected goods.',
  'stok.gr.rejected': 'Rejected ({unit})',
  'stok.gr.rejectReason': 'Reason for rejecting',
  'stok.gr.rejectReasonHint': 'E.g. damaged, wrong item, below quality.',
  'stok.gr.lotCode': 'Lot code',
  'stok.gr.lotHint': 'Leave empty and the system creates one from the receipt number.',
  'stok.gr.expiry': 'Expiry date',
  'stok.gr.expiryHint': 'As printed on the package. Required before posting.',
  'stok.gr.expirySuggested': 'Suggestion: today + shelf life of {days} days — check the label.',
  'stok.gr.manufactured': 'Production date',
  'stok.gr.manufacturedOn': 'produced {date}',
  'stok.gr.hold': 'Quality hold',
  'stok.gr.holdHint': 'The lot is not used until it is released.',
  'stok.gr.acceptedPreview': 'Accepted: {qty}',
  'stok.gr.value': 'Value',
  'stok.gr.acceptedTotal': 'Value of accepted goods',
  'stok.gr.newNote': 'Saved as a draft first; post it once everything is checked.',
  'stok.gr.draftSavedNote': 'Posting adds the accepted goods to stock and cannot be undone.',
  'stok.gr.saveDraft': 'Save draft',
  'stok.gr.created': 'Receipt {number} saved as a draft.',
  'stok.gr.saved': 'Receipt {number} saved.',
  'stok.gr.post': 'Post',
  'stok.gr.posting': 'Posting…',
  'stok.gr.postTitle': 'Post receipt {number}?',
  'stok.gr.postBody': '{count} lines will be posted to {location}.',
  'stok.gr.postSavesFirst': 'Your changes are saved first.',
  'stok.gr.postWarning':
    'Stock quantity and value change, and the receipt can no longer be edited.',
  'stok.gr.posted': 'Receipt {number} posted ({count} movements).',
  'stok.gr.cancelDraft': 'Cancel draft',
  'stok.gr.cancelling': 'Cancelling…',
  'stok.gr.cancelTitle': 'Cancel draft {number}?',
  'stok.gr.cancelBody': 'The draft is kept as cancelled. Stock does not change.',
  'stok.gr.cancelWarning': 'A cancelled receipt cannot be posted.',
  'stok.gr.cancelled': 'Receipt {number} cancelled.',
  'stok.gr.factsTitle': 'Receipt',
  'stok.gr.factsDesc': 'Where the goods went and who recorded and posted them.',
  'stok.gr.createdBy': 'Recorded by',
  'stok.gr.postedBy': 'Posted by',
  'stok.gr.businessDate': 'Business date',
  'stok.gr.noLines': 'This receipt has no lines.',
  'stok.gr.colDelivered': 'Delivered',
  'stok.gr.colRejected': 'Rejected',
  'stok.gr.colAccepted': 'Accepted',
  'stok.gr.lotAuto': 'automatic',
  'stok.gr.onHold': 'On hold',
  'stok.gr.resultTitle': 'Receipt posted',
  'stok.gr.resultDesc': 'These stock movements were created.',
  'stok.gr.resultEmpty': 'No stock movement — everything delivered was rejected.',
  'stok.gr.viewLedger': 'View movements',
  'stok.gr.err.location': 'Choose a location.',
  'stok.gr.err.tooLong': 'Use at most {max} characters.',
  'stok.gr.err.qty': 'Enter a quantity (at most 3 decimals).',
  'stok.gr.err.precision': 'This quantity cannot be converted exactly to the base unit.',
  'stok.gr.err.rejectedTooMuch': 'Rejected cannot be more than delivered.',
  'stok.gr.err.reason': 'Enter why goods were rejected.',
  'stok.gr.err.overRemaining': 'Accepted is more than the remaining quantity of the order.',
  'stok.gr.err.overReceipt':
    'More than ordered: ordered {ordered}, already received {received}, remaining {remaining}.',
  'stok.gr.err.overOrdered': 'More than ordered ({ordered}) would be received.',
  'stok.gr.err.overReceiptAlert': 'A line receives more than was ordered — check the marked line.',
  'stok.gr.err.tooLarge': 'The amount is too large.',
  'stok.gr.err.date': 'Enter a complete date.',
  'stok.gr.err.expiryRequired': 'Enter the expiry date before posting.',
  'stok.gr.err.lotExpiryConflict': 'This lot code already exists with another expiry date.',
  'stok.gr.err.noLines': 'Enter the delivered quantity of at least one line.',
} as const;
